import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ukWhen } from "@/lib/time";
import { gbp } from "@/lib/stages";
import { Btn, CellMain, CellSub, Empty, Inp, Panel, St, Tb, Td, Th } from "@/components/portal/crm/kit";
import { SeatLeaderboard } from "@/components/portal/SeatLeaderboard";
import { toast } from "sonner";

const num = { fontVariantNumeric: "tabular-nums" };

/** The holder's internal share for one agent. Click to set it; 0 clears it. */
function SplitCell({ seat, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(seat.split_pct ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(seat.split_pct ?? ""); }, [seat.split_pct]);

  const save = async () => {
    const n = val === "" ? 0 : Math.max(0, Math.min(100, parseFloat(val) || 0));
    setBusy(true);
    try { await onSave(seat, n); setEditing(false); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <span data-testid={`referrer-seat-split-cell-${seat.id}`}>
      <form onSubmit={(e) => { e.preventDefault(); save(); }} data-testid={`referrer-seat-split-form-${seat.id}`}
            style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
        <input type="number" min="0" max="100" step="1" value={val} onChange={(e) => setVal(e.target.value)} autoFocus
               placeholder="0" aria-label="Their share of your commission" className="inp"
               style={{ width: 62, height: 30, padding: "0 8px", textAlign: "right", ...num }}
               data-testid={`referrer-seat-split-input-${seat.id}`} />
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy} data-testid={`referrer-seat-split-save-${seat.id}`}>Set</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setVal(seat.split_pct ?? ""); }}
                data-testid={`referrer-seat-split-cancel-${seat.id}`}>Cancel</button>
      </form>
      </span>
    );
  }

  const pct = seat.split_pct || 0;
  return (
    <span data-testid={`referrer-seat-split-cell-${seat.id}`}>
    <button type="button" onClick={() => setEditing(true)} data-testid={`referrer-seat-split-${seat.id}`}
            title={pct ? "Change their share" : "Give them a share of your commission"}
            style={{ border: 0, background: "none", padding: 0, font: "inherit", cursor: "pointer", textAlign: "right", width: "100%" }}>
      <CellMain style={{ ...num, color: pct ? "var(--ink)" : "var(--mut)", fontWeight: pct ? 700 : 500 }}>
        {pct ? `${pct}%` : "set a share"}
      </CellMain>
      {pct ? <CellSub style={{ textAlign: "right" }}>of your commission</CellSub> : null}
    </button>
    </span>
  );
}

/** People & access — the seats an introducer puts on their own portal (call-centre agents, team). */
export function TeamSeats({ title = "People & access" }) {
  const [items, setItems] = useState(null);
  const [splits, setSplits] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [working, setWorking] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await api.get("/refer/seats")).data.items || []); }
    catch { setItems([]); }
    try {
      const d = (await api.get("/refer/seat-splits")).data;
      const m = {};
      (d.seats || []).forEach((s) => { m[s.seat_id] = s; });
      setSplits(m);
    } catch { setSplits({}); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("A name and an email, please"); return; }
    setBusy(true);
    try {
      const r = await api.post("/refer/seats", { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() || null });
      toast.success(r.data.invite_emailed ? `${r.data.name} has been emailed their set-up link` : `${r.data.name} added — copy their set-up link below`);
      setForm({ name: "", email: "", phone: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't add that seat"); }
    finally { setBusy(false); }
  };

  const saveSplit = async (seat, n) => {
    try {
      await api.patch(`/refer/seats/${seat.id}`, { split_pct: n });
      toast.success(n ? `${seat.name} now takes ${n}% of your commission on the cases they put in`
                      : `${seat.name}'s share cleared`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save that share"); throw err; }
  };

  const act = async (seat, what) => {
    setWorking(seat.id);
    try {
      if (what === "resend") { const r = await api.post(`/refer/seats/${seat.id}/invite`); toast.success(r.data.invite_emailed ? "Set-up link sent again" : "New link ready to copy"); }
      else if (what === "pause") { await api.patch(`/refer/seats/${seat.id}`, { status: "paused" }); toast.success(`${seat.name} paused — they can't sign in until you switch them back on`); }
      else if (what === "resume") { await api.patch(`/refer/seats/${seat.id}`, { status: "active" }); toast.success(`${seat.name} is back on`); }
      else if (what === "remove") {
        if (!window.confirm(`Remove ${seat.name}'s seat? Their sign-in stops now. The cases they put in stay on your book.`)) return;
        const r = await api.delete(`/refer/seats/${seat.id}`);
        toast.success(`Seat removed · ${r.data.cases_kept} case${r.data.cases_kept === 1 ? "" : "s"} kept on your book`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "That didn't go through"); }
    finally { setWorking(null); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Set-up link copied"); } catch { toast.error("Copy failed"); }
  };

  return (
    <>
    <Panel title={title} pad={false} testId="referrer-seats"
           sub="Give your agents their own sign-in. Each one sees only the cases they put in, and you can hand them a share of your commission on those cases."
           footer={<span>Every case an agent submits is attributed to your company and earns <b>your</b> commission — their share is an internal split you settle with them, never a separate payment from Connexis. It shows on your monthly statement so you have a record.</span>}>
      <form onSubmit={invite} className="seat-form" data-testid="referrer-seat-form">
        <Inp placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="referrer-seat-name" />
        <Inp type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="referrer-seat-email" />
        <Inp placeholder="Mobile (optional)" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="referrer-seat-phone" />
        <Btn kind="primary" type="submit" disabled={busy} testId="referrer-seat-invite">{busy ? "Sending…" : "Add a seat"}</Btn>
      </form>

      {items === null ? (
        <Empty testId="referrer-seats-loading">Reading your team…</Empty>
      ) : items.length === 0 ? (
        <Empty testId="referrer-seats-empty">No seats yet. Add a name and an email above — they get a set-up link within a minute.</Empty>
      ) : (
        <Tb testId="referrer-seats-table" head={<>
          <Th style={{ width: "28%" }}>Person</Th>
          <Th style={{ width: "13%" }}>Access</Th>
          <Th className="r" style={{ width: "8%" }}>Cases</Th>
          <Th className="r" style={{ width: "7%" }}>Live</Th>
          <Th className="r" style={{ width: "14%" }}>Their share</Th>
          <Th className="r" style={{ width: "12%" }}>Earned</Th>
          <Th className="r" style={{ width: "18%" }}>&nbsp;</Th>
        </>}>
          {items.map((s) => {
            const sp = splits[s.id] || {};
            return (
            <tr key={s.id} data-testid={`referrer-seat-row-${s.id}`}>
              <Td className="lead">
                <CellMain>{s.name}</CellMain>
                <CellSub>{s.email}</CellSub>
                <CellSub>{s.last_seen ? `Last in ${ukWhen(s.last_seen)}` : s.activated ? "Not signed in yet" : "No password yet"}</CellSub>
              </Td>
              <Td>
                <St tone={s.status === "paused" ? "slate" : s.activated ? "mint" : "ink"} testId={`referrer-seat-status-${s.id}`}>
                  {s.status === "paused" ? "Paused" : s.activated ? "Active" : "Invited"}
                </St>
                {!s.activated && s.status !== "paused" && (
                  <CellSub style={{ marginTop: 5 }}>
                    <button type="button" onClick={() => copy(s.invite_url)} data-testid={`referrer-seat-copy-${s.id}`}
                            style={{ border: 0, background: "none", padding: 0, font: "inherit", cursor: "pointer", color: "var(--accent-text)", textDecoration: "underline", textDecorationStyle: "dotted" }}>
                      Copy set-up link
                    </button>
                  </CellSub>
                )}
              </Td>
              <Td className="r"><CellMain style={num}>{s.cases}</CellMain></Td>
              <Td className="r"><CellMain style={num}>{s.live_cases}</CellMain></Td>
              <Td className="r"><SplitCell seat={s} onSave={saveSplit} /></Td>
              <Td className="r" data-testid={`referrer-seat-earned-${s.id}`}>
                <CellMain style={{ ...num, color: sp.agent_gbp ? "var(--ink)" : "var(--mut)", fontWeight: sp.agent_gbp ? 700 : 500 }}>
                  {sp.agent_gbp ? gbp(sp.agent_gbp) : "—"}
                </CellMain>
                {sp.owed_gbp ? <CellSub style={{ textAlign: "right" }}>{gbp(sp.owed_gbp)} to settle</CellSub> : null}
              </Td>
              <Td className="r">
                <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {!s.activated && <Btn sm disabled={working === s.id} onClick={() => act(s, "resend")} testId={`referrer-seat-resend-${s.id}`}>Resend link</Btn>}
                  {s.status === "paused"
                    ? <Btn sm disabled={working === s.id} onClick={() => act(s, "resume")} testId={`referrer-seat-resume-${s.id}`}>Switch on</Btn>
                    : <Btn sm disabled={working === s.id} onClick={() => act(s, "pause")} testId={`referrer-seat-pause-${s.id}`}>Pause</Btn>}
                  <Btn sm disabled={working === s.id} onClick={() => act(s, "remove")} testId={`referrer-seat-remove-${s.id}`}>Remove</Btn>
                </div>
              </Td>
            </tr>
          ); })}
        </Tb>
      )}
    </Panel>
    <SeatLeaderboard refreshKey={items} />
    </>
  );
}
